import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { useCommonUI } from "../contexts/CommonUIContext";
import TopBar from "../components/TopBar";
import DropdownMenu from "../components/DropdownMenu";
import ProjectChip from "../components/ProjectChip";
import NotificationBell from "../components/NotificationBell";
import DynamicTabSelector from "../components/DynamicTabSelector";
import LabelFilterStrip from "../components/LabelFilterStrip";
import { useManageLabels } from "../hooks/useManageLabels";
import AppInfoDialog from "../components/AppInfoDialog";
import accountIcon from "../assets/icon_account.svg";
import infoIcon from "../assets/icon_info.svg";
import logoutIcon from "../assets/icon_logout.svg";
import folderAddIcon from "../assets/icon_folder_add.svg";
import folderOpenIcon from "../assets/icon_folder_open.svg";
import { isAllProjects } from "../constants/projects";
import {
  getCurrencyInfo,
  getHourlyRate,
  getAiHourlyRate,
  isAiInvoiceEnabled,
} from "../utils/billingRateSetting";
import {
  RANGE_PRESETS,
  rangeForPreset,
  computeStats,
  formatDuration,
  formatDateLong,
  formatDayHeader,
  weekdayLabel,
  niceMaxHours,
  dayKey,
  startOfDay,
  costFor,
} from "../utils/statsCompute";
import { generateBillingPdf } from "../utils/billingPdf";

const DANGER = "#e5484d";
const HUMAN = "var(--track-human, #7b76ff)";
const AI = "var(--track-ai, #3b82f6)";

function StatsScreen() {
  const navigate = useNavigate();
  const {
    firebaseAuth,
    currentUser,
    firebaseFirestore,
    projects,
    activeProject,
    handleProjectChange,
    labels,
    startCollectingLabels,
    allTimeRecords,
    tasks,
    startCollectingTimeRecords,
    createNewProject,
  } = useContext(FirebaseContext);
  const { openModal } = useCommonUI();

  const [mode, setMode] = useState(0); // 0 = Graph, 1 = Detail
  const [presetIdx, setPresetIdx] = useState(0);
  const [range, setRange] = useState(() => rangeForPreset(RANGE_PRESETS[0].days));
  const [selectedLabelIds, setSelectedLabelIds] = useState(new Set());
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [seededExpansion, setSeededExpansion] = useState(false);
  const [actionLabelById, setActionLabelById] = useState({});

  useEffect(() => {
    if (startCollectingTimeRecords) startCollectingTimeRecords();
    if (startCollectingLabels) startCollectingLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-shot load of all actions so the Detail rows can show action labels.
  useEffect(() => {
    if (!currentUser || !firebaseFirestore) return;
    (async () => {
      try {
        const snap = await getDocs(
          collection(firebaseFirestore, `users/${currentUser.uid}/actions`)
        );
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = d.data().actionLabel || "";
        });
        setActionLabelById(map);
      } catch (e) {
        console.error("Failed to load actions for stats:", e);
      }
    })();
  }, [currentUser, firebaseFirestore]);

  const selectPreset = (i) => {
    setPresetIdx(i);
    setRange(rangeForPreset(RANGE_PRESETS[i].days));
  };

  // Custom range: editing Start/End deselects the presets (presetIdx = -1),
  // mirroring Android's CUSTOM preset. Keep start <= end.
  const parseInputDate = (value) => {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const setStart = (value) => {
    if (!value) return;
    const start = parseInputDate(value);
    const end = range.endDate < start ? new Date(start) : range.endDate;
    setPresetIdx(-1);
    setRange({ startDate: start, endDate: end });
  };
  const setEnd = (value) => {
    if (!value) return;
    const end = parseInputDate(value);
    const start = range.startDate > end ? new Date(end) : range.startDate;
    setPresetIdx(-1);
    setRange({ startDate: start, endDate: end });
  };

  const tasksById = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => m.set(Number(t.id), t));
    return m;
  }, [tasks]);
  const projectsById = useMemo(() => {
    const m = new Map();
    projects.forEach((p) => m.set(Number(p.id), p));
    return m;
  }, [projects]);

  const projectIds = useMemo(() => {
    if (!activeProject || isAllProjects(activeProject.id)) return null;
    return new Set([Number(activeProject.id)]);
  }, [activeProject]);

  // Same label + action-template management dialog as Home / Task detail.
  const openManageLabels = useManageLabels(
    activeProject && !isAllProjects(activeProject.id) ? Number(activeProject.id) : null
  );

  const hourlyRate = getHourlyRate();
  const aiHourlyRate = getAiHourlyRate();
  const aiInvoiceEnabled = isAiInvoiceEnabled();
  const currency = getCurrencyInfo();

  const stats = useMemo(
    () =>
      computeStats({
        records: allTimeRecords,
        tasksById,
        projectsById,
        actionLabelById,
        startDate: range.startDate,
        endDate: range.endDate,
        projectIds,
        labelIds: selectedLabelIds,
        hourlyRate,
        aiHourlyRate,
        aiInvoiceEnabled,
      }),
    [allTimeRecords, tasksById, projectsById, actionLabelById, range, projectIds, selectedLabelIds, hourlyRate, aiHourlyRate, aiInvoiceEnabled]
  );

  // Default-expand the most recent day once the breakdown first arrives.
  useEffect(() => {
    if (!seededExpansion && stats.breakdown.length > 0) {
      setExpandedDays(new Set([stats.breakdown[0].key]));
      setSeededExpansion(true);
    }
  }, [stats.breakdown, seededExpansion]);

  const onDayTap = (key) => {
    setMode(1);
    setExpandedDays(new Set([key]));
  };
  const toggleDay = (key) =>
    setExpandedDays((prev) => (prev.has(key) ? new Set() : new Set([key])));

  const showValue = hourlyRate > 0 || aiHourlyRate > 0;
  const fmtMoney = (v) => `${currency.symbol}${v.toFixed(2)} ${currency.code}`;

  // Re-run the bar grow animation only on "fresh" data — range, project, or
  // label change — NOT on same-range live ticks (today's bar growing). Mirrors
  // Android's restartKey.
  const animKey = useMemo(
    () =>
      [
        range.startDate.getTime(),
        range.endDate.getTime(),
        projectIds ? [...projectIds].join(",") : "all",
        [...selectedLabelIds].sort().join(","),
      ].join("|"),
    [range, projectIds, selectedLabelIds]
  );

  const projectName =
    !activeProject || isAllProjects(activeProject.id) ? "All Projects" : activeProject.projectName;

  const handleDownload = () => {
    const labelNames = labels
      .filter((l) => selectedLabelIds.has(Number(l.id)))
      .map((l) => l.name);
    generateBillingPdf({
      projectName,
      currencySymbol: currency.symbol,
      rate: hourlyRate,
      aiRate: aiInvoiceEnabled ? aiHourlyRate : 0,
      rows: stats.pdfRows,
      startDate: range.startDate,
      endDate: range.endDate,
      labelNames,
    });
  };

  // ── top bar wiring (account menu + project actions) ───────────────────────
  const handleLogout = async () => {
    try {
      await signOut(firebaseAuth);
      navigate("/auth");
    } catch (e) {
      console.error("Sign out failed:", e);
    }
  };
  const accountMenuItems = [
    { icon: infoIcon, label: "App Information", onClick: () => openModal(<AppInfoDialog />) },
    { icon: logoutIcon, label: "Sign Out", color: DANGER, onClick: handleLogout },
  ];
  const projectActionItems = [
    {
      icon: folderAddIcon,
      label: "Add Project",
      onClick: async () => {
        const id = await createNewProject();
        if (id) navigate(`/projectDetail/${id}`);
      },
    },
  ];
  if (activeProject && !isAllProjects(activeProject.id)) {
    projectActionItems.push({
      icon: folderOpenIcon,
      label: `Edit ${activeProject.projectName}`,
      onClick: () => navigate(`/projectDetail/${activeProject.id}`),
    });
  }

  return (
    <div style={styles.container}>
      <TopBar
        leftContainer={
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <DropdownMenu icon={accountIcon} menuItems={accountMenuItems} />
            <NotificationBell />
          </div>
        }
        rightContainer={
          <ProjectChip
            projects={projects}
            activeProject={activeProject}
            onProjectChange={handleProjectChange}
            actionItems={projectActionItems}
          />
        }
      />

      <div style={styles.body}>
        <DynamicTabSelector
          tabs={["Graph", "Detail"]}
          selectedTabIndex={mode}
          onTabSelected={setMode}
          style={{ position: "relative", top: "auto", left: "auto", right: "auto", width: "auto", flexShrink: 0 }}
        />

        {/* Range presets */}
        <div style={styles.pillRow}>
          {RANGE_PRESETS.map((p, i) => (
            <button
              key={p.key}
              onClick={() => selectPreset(i)}
              style={presetIdx === i ? styles.pillOn : styles.pill}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Start / End — click to pick a custom date (deselects presets). */}
        <div style={styles.dateRow}>
          <label style={styles.dateField}>
            <div style={styles.muted}>Start</div>
            <div style={styles.dateValue}>{formatDateLong(range.startDate)}</div>
            <input
              type="date"
              value={dayKey(range.startDate)}
              max={dayKey(range.endDate)}
              onChange={(e) => setStart(e.target.value)}
              style={styles.dateInput}
            />
          </label>
          <label style={styles.dateField}>
            <div style={styles.muted}>End</div>
            <div style={styles.dateValue}>{formatDateLong(range.endDate)}</div>
            <input
              type="date"
              value={dayKey(range.endDate)}
              min={dayKey(range.startDate)}
              onChange={(e) => setEnd(e.target.value)}
              style={styles.dateInput}
            />
          </label>
        </div>

        {/* Label filter */}
        <LabelFilterStrip
          labels={labels}
          selectedIds={selectedLabelIds}
          onToggle={(id) =>
            setSelectedLabelIds((prev) => {
              const n = Number(id);
              const next = new Set(prev);
              if (next.has(n)) next.delete(n);
              else next.add(n);
              return next;
            })
          }
          onClearAll={() => setSelectedLabelIds(new Set())}
          onAddLabel={openManageLabels}
        />

        {/* Summary */}
        <div style={styles.summaryRow}>
          <Summary label="Today" value={formatDuration(stats.todayMs)} />
          <Summary label="Range Total" value={formatDuration(stats.rangeTotalMs)} />
          {showValue && (
            <Summary label="Range Value" value={fmtMoney(stats.rangeValue)} accent />
          )}
        </div>

        <div style={styles.scroll}>
          {mode === 0 ? (
            <BarChart dayTotals={stats.dayTotals} onDayTap={onDayTap} animationKey={animKey} />
          ) : (
            <DetailList
              breakdown={stats.breakdown}
              expandedDays={expandedDays}
              toggleDay={toggleDay}
              showValue={showValue}
              hourlyRate={hourlyRate}
              aiHourlyRate={aiHourlyRate}
              fmtMoney={fmtMoney}
            />
          )}
        </div>
      </div>

      {/* Download report — pinned above the bottom bar */}
      <div style={styles.footer}>
        <button className="stats-download-btn" style={styles.downloadBtn} onClick={handleDownload}>
          Download {projectName} Report
        </button>
      </div>
    </div>
  );
}

function Summary({ label, value, accent }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={styles.muted}>{label}</div>
      <div style={{ ...styles.summaryValue, color: accent ? "var(--primary)" : "var(--text)" }}>
        {value}
      </div>
    </div>
  );
}

function BarChart({ dayTotals, onDayTap, animationKey }) {
  const barsRef = useRef(null);
  // Auto-scroll to the most recent (rightmost) day on load / range change,
  // mirroring Android's auto-scroll-to-today for long ranges.
  useEffect(() => {
    if (barsRef.current) barsRef.current.scrollLeft = barsRef.current.scrollWidth;
  }, [animationKey, dayTotals.length]);

  const todayKey = dayKey(startOfDay(new Date()));
  const maxHours = Math.max(0, ...dayTotals.map((d) => d.totalMs / 3_600_000));
  const niceMax = niceMaxHours(maxHours);
  const H = 200; // px bar area
  const pct = (ms) => Math.min(1, ms / 3_600_000 / niceMax);

  if (dayTotals.length === 0) {
    return <div style={styles.empty}>No tracked time in this range.</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", marginTop: "8px" }}>
        {/* Y axis */}
        <div style={{ ...styles.yAxis, height: H }}>
          <span>{niceMax}h</span>
          <span>{niceMax / 2}h</span>
          <span>0</span>
        </div>
        {/* Bars — keyed on animationKey so the grow animation replays on a
            range/project/label change (not on live same-range updates). Bars
            keep a fixed slot width and scroll horizontally when there are too
            many to fit (so 1-month and 3-month bars are the same thickness). */}
        <div ref={barsRef} key={animationKey} style={{ ...styles.bars, height: H }}>
          {dayTotals.map((d) => {
            const isToday = d.key === todayKey;
            const both = d.humanMs > 0 && d.aiMs > 0;
            const op = isToday ? 1 : 0.55;
            return (
              <div
                key={d.key}
                style={styles.barCol}
                onClick={() => onDayTap(d.key)}
                title={formatDuration(d.totalMs)}
              >
                <div style={{ ...styles.barArea, height: H }}>
                  {both ? (
                    <div style={styles.barPair}>
                      <div className="stats-bar-grow" style={{ ...styles.bar, width: "42%", height: `${pct(d.humanMs) * 100}%`, background: HUMAN, opacity: op }} />
                      <div className="stats-bar-grow" style={{ ...styles.bar, width: "42%", height: `${pct(d.aiMs) * 100}%`, background: AI, opacity: op }} />
                    </div>
                  ) : (
                    <div
                      className="stats-bar-grow"
                      style={{
                        ...styles.bar,
                        width: "60%",
                        height: `${pct(d.humanMs + d.aiMs) * 100}%`,
                        background: d.aiMs > 0 ? AI : HUMAN,
                        opacity: op,
                      }}
                    />
                  )}
                </div>
                <div style={{ ...styles.dayLabel, color: isToday ? "var(--primary)" : "var(--text-muted)" }}>
                  {weekdayLabel(d.date)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={styles.hint}>Tap a bar to see that day in detail.</div>
    </div>
  );
}

function DetailList({ breakdown, expandedDays, toggleDay, showValue, hourlyRate, aiHourlyRate, fmtMoney }) {
  if (breakdown.length === 0) {
    return <div style={styles.empty}>No tracked time in this range.</div>;
  }
  return (
    <div>
      {breakdown.map((day) => {
        const expanded = expandedDays.has(day.key);
        const dayCost = day.rows.reduce(
          (s, r) => s + costFor(r.durationMs, r.trackerKind === "ai" ? aiHourlyRate : hourlyRate),
          0
        );
        const both = day.humanMs > 0 && day.aiMs > 0;
        return (
          <div key={day.key} style={styles.dayBlock}>
            <div style={styles.dayHeader} onClick={() => toggleDay(day.key)}>
              <span style={styles.caret}>{expanded ? "▾" : "▸"}</span>
              <span style={styles.dayTitle}>{formatDayHeader(day.date)}</span>
              <span style={{ flex: 1 }} />
              <span style={styles.dayTotal}>{formatDuration(day.totalMs)}</span>
              {showValue && <span style={styles.dayCost}>{fmtMoney(dayCost)}</span>}
            </div>
            {both && (
              <div style={styles.splitLine}>
                <span style={{ color: HUMAN }}>Human {formatDuration(day.humanMs)}</span>
                <span style={{ color: "var(--text-muted)" }}> · </span>
                <span style={{ color: AI }}>AI {formatDuration(day.aiMs)}</span>
              </div>
            )}
            {expanded &&
              day.rows.map((r) => {
                const cost = costFor(r.durationMs, r.trackerKind === "ai" ? aiHourlyRate : hourlyRate);
                return (
                  <div key={r.timeRecordId} style={styles.sessionRow}>
                    <span style={{ ...styles.dot, background: r.trackerKind === "ai" ? AI : HUMAN }} />
                    {r.trackerKind === "ai" && <span style={styles.aiBadge}>AI</span>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.sessProject}>{r.projectName || "—"}</div>
                      <div style={styles.sessTask}>{r.taskTitle || "—"}</div>
                      {r.actionLabel ? <div style={styles.sessAction}>{r.actionLabel}</div> : null}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={styles.sessDuration}>{formatDuration(r.durationMs)}</div>
                      {showValue && <div style={styles.sessCost}>{fmtMoney(cost)}</div>}
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}

const PILL = { padding: "5px 12px", borderRadius: "16px", cursor: "pointer", fontSize: "12px", whiteSpace: "nowrap" };
const styles = {
  container: { paddingTop: "50px", height: "calc(100vh - 60px)", boxSizing: "border-box", display: "flex", flexDirection: "column" },
  body: { padding: "12px 16px 0", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
  pillRow: { display: "flex", gap: "8px", margin: "12px 0 4px", overflowX: "auto" },
  pill: { ...PILL, border: "1px solid var(--border)", background: "transparent", color: "var(--text)" },
  pillOn: { ...PILL, border: "1px solid var(--primary)", background: "var(--primary)", color: "#fff" },
  dateRow: { display: "flex", gap: "48px", margin: "8px 0" },
  muted: { fontSize: "11px", color: "var(--text-muted)" },
  dateValue: { fontSize: "16px", fontWeight: 700, color: "var(--text)" },
  dateField: { position: "relative", display: "inline-block", cursor: "pointer" },
  dateInput: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    opacity: 0,
    cursor: "pointer",
    colorScheme: "dark",
  },
  summaryRow: { display: "flex", gap: "20px", margin: "12px 0", flexWrap: "wrap" },
  summaryValue: { fontSize: "18px", fontWeight: 700 },
  scroll: { flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: "88px" },
  yAxis: { display: "flex", flexDirection: "column", justifyContent: "space-between", width: "34px", fontSize: "10px", color: "var(--text-muted)", paddingRight: "6px", boxSizing: "border-box", borderRight: "1px solid var(--border)" },
  bars: { flex: 1, display: "flex", alignItems: "flex-end", gap: "4px", borderBottom: "1px solid var(--border)", overflowX: "auto", overflowY: "hidden" },
  // Min slot width: <=~7 days stretch to fill; longer ranges keep this width
  // and the row scrolls (so 1-month and 3-month bars are equally thick).
  barCol: { flex: "1 0 40px", minWidth: "40px", display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" },
  barArea: { width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center" },
  barPair: { width: "100%", height: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "6%" },
  bar: { borderRadius: "3px 3px 0 0", minHeight: "2px" },
  dayLabel: { fontSize: "10px", marginTop: "4px" },
  hint: { fontSize: "11px", color: "var(--text-muted)", marginTop: "8px" },
  empty: { color: "var(--text-muted)", fontSize: "14px", padding: "24px 0" },
  dayBlock: { borderBottom: "1px solid var(--border)", padding: "10px 0" },
  dayHeader: { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" },
  caret: { color: "var(--text-muted)", width: "12px" },
  dayTitle: { fontWeight: 700, color: "var(--text)" },
  dayTotal: { color: "var(--text)" },
  dayCost: { color: "var(--primary)", marginLeft: "10px" },
  splitLine: { fontSize: "11px", marginTop: "4px", marginLeft: "20px" },
  sessionRow: { display: "flex", alignItems: "center", gap: "8px", padding: "8px 0 8px 20px" },
  dot: { width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0 },
  aiBadge: { fontSize: "9px", fontWeight: 700, color: AI, border: `1px solid ${AI}`, borderRadius: "4px", padding: "0 3px" },
  sessProject: { fontSize: "11px", color: "var(--text-muted)" },
  sessTask: { fontSize: "13px", color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sessAction: { fontSize: "11px", color: "var(--primary)" },
  sessDuration: { fontSize: "13px", color: "var(--text)" },
  sessCost: { fontSize: "11px", color: "var(--primary)" },
  footer: { position: "fixed", left: 0, right: 0, bottom: "60px", padding: "12px 16px", background: "var(--bg)", borderTop: "1px solid var(--border)", boxSizing: "border-box", zIndex: 900, textAlign: "center" },
  // width + padding come from the .stats-download-btn class (responsive):
  // content-width + centered on desktop, full-width on mobile.
  downloadBtn: { border: "none", borderRadius: "10px", background: "var(--primary)", color: "#fff", fontSize: "15px", fontWeight: 600, cursor: "pointer" },
};

export default StatsScreen;
